import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/zh-cn';
import i18next from 'i18next';
import zhConvertor from 'zhconvertor';
import { IoClose } from 'react-icons/io5';
import { Gpt } from 'react-gpt-ads';
import { trackEvent } from '../../utils/analytics';
import { recordTag } from '../../utils/interestTracker';
import VideoSection from '../Home/VideoSection';
import './PartyArticlesPanel.css';

const tr = (text) =>
  localStorage.getItem('lang') !== 'zh_TW' ? zhConvertor.t2s(text) : text;

const PartyArticlesPanel = ({ tag, fallbackTag = null, location = 'party_panel', onClose }) => {
  const open = Boolean(tag);
  const [mounted, setMounted] = useState(false);
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  // The term whose results are actually shown (the primary tag, or the fallback
  // when the primary returned nothing).
  const [displayTerm, setDisplayTerm] = useState(null);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const lang = localStorage.getItem('lang') || 'zh_CN';
    axios
      .get(`article_with_gallery.php?lang=${lang}`)
      .then((res) => {
        if (!cancelled) setVideos(res.data?.videos || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => setMounted(true));
      return () => cancelAnimationFrame(id);
    }
    setMounted(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!tag) {
      setArticles([]);
      setDisplayTerm(null);
      return;
    }
    let cancelled = false;
    setArticlesLoading(true);
    setArticles([]);
    setDisplayTerm(tag); // show the clicked term while loading
    trackEvent('article_tag', { tag, location });
    recordTag(tag);
    const lang = localStorage.getItem('lang') || 'zh_CN';
    const fetchTag = (t) =>
      axios
        .get(`article_tag.php?tag=${encodeURIComponent(t)}&lang=${lang}`, {
          timeout: 7000,
        })
        .then((response) => {
          const data = response.data;
          if (data && data.status === 'success' && Array.isArray(data.articles)) {
            return data.articles;
          }
          return [];
        });

    (async () => {
      try {
        let arts = await fetchTag(tag);
        let term = tag;
        if (arts.length === 0 && fallbackTag && fallbackTag !== tag) {
          arts = await fetchTag(fallbackTag);
          term = fallbackTag;
        }
        if (cancelled) return;
        setArticles(arts);
        setDisplayTerm(term);
      } catch {
        if (!cancelled) setArticles([]);
      } finally {
        if (!cancelled) setArticlesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tag, fallbackTag, location]);

  if (!open) return null;

  return createPortal(
    <>
      <div
        className={`party_panel_backdrop ${mounted ? 'open' : ''}`}
        onClick={onClose}
      />
      <div
        className={`party_panel ${mounted ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="party_panel_handle" />
        <div className="party_panel_header">
          <div className="party_panel_title">
            <span className="party_panel_partyname">{displayTerm}</span>
            <span className="party_panel_subtitle">{tr('相关报道')}</span>
          </div>
          <button
            type="button"
            className="party_panel_close"
            aria-label="Close"
            onClick={onClose}
          >
            <IoClose />
          </button>
        </div>

        <div className="party_panel_body">
          {articlesLoading && articles.length === 0 ? (
            <div className="seat_panel_articles_loading">
              {tr('载入中…')}
            </div>
          ) : articles.length === 0 ? (
            <div className="party_panel_empty">{tr('暂无相关报道')}</div>
          ) : (
            <>
              <ul className="seat_panel_articles_list">
                {articles.map((art, i) => (
                  <React.Fragment key={art.id}>
                    <li className="seat_panel_article_item">
                      <a
                        href={art.url}
                        target="_blank"
                        rel="noreferrer"
                        className="seat_panel_article_link"
                        onClick={() => trackEvent('article_click', { url: art.url, title: art.title, location })}
                      >
                        {art.picurl && (
                          <img
                            src={art.picurl}
                            alt=""
                            className="seat_panel_article_thumb"
                            loading="lazy"
                          />
                        )}
                        <div className="seat_panel_article_text">
                          <div className="seat_panel_article_title">
                            {art.title}
                            {art.type === 'gallery' && (
                              <span className="seat_panel_article_tag">
                                {i18next.t('图集')}
                              </span>
                            )}
                          </div>
                          {art.date && (
                            <div className="seat_panel_article_date">
                              {moment(art.date).fromNow()}
                            </div>
                          )}
                        </div>
                      </a>
                    </li>
                    {i === Math.min(7, articles.length - 1) && videos.length > 0 && (
                      <li className="seat_panel_video_wrap">
                        <VideoSection videos={videos} />
                      </li>
                    )}
                    {i === 1 && (
                      <li className="seat_panel_ad" aria-label="Advertisement">
                        <Gpt
                          adUnit="/2163006/election_sidepop"
                          name="div-gpt-ad-1782135854676-0"
                          size={['fluid', [300, 250], [300, 100], [336, 280]]}
                        />
                      </li>
                    )}
                  </React.Fragment>
                ))}
              </ul>
              <a
                href={`https://www.orientaldaily.com.my/news/browse/${encodeURIComponent(displayTerm)}`}
                target="_blank"
                rel="noreferrer"
                className="seat_panel_articles_more"
              >
                {tr(`更多 ${displayTerm} 相关新闻`)} →
              </a>
            </>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default PartyArticlesPanel;
